import React, { useEffect, useState, useRef } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Oauth2GoogleService } from '../hop-dong/mau-hop-dong/Oauth2GoogleService';
import { AgreementService } from '../../service/AgreementService';

// Cấu hình worker cho PDF.js
GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';

interface PdfCanvasViewerProps {
    fileId?: string | null;
    blob?: Blob | null;
    fileName?: string;
    agreementId?: string;
    onLoadComplete?: () => void;
    onLoadError?: (error: any) => void;
    scale?: number;
    currentPage?: number;
    onPageChange?: (page: number) => void;
}

export const PdfCanvasViewer: React.FC<PdfCanvasViewerProps> = ({
    fileId,
    blob,
    fileName = 'document.pdf',
    agreementId,
    onLoadComplete,
    onLoadError,
    scale = 1.5,
    currentPage = 1,
    onPageChange
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [numPages, setNumPages] = useState<number>(0);
    const [thumbnails, setThumbnails] = useState<string[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const renderTaskRef = useRef<any[]>([]);

    const scrollToPage = (pageNumber: number) => {
        const pageElement = containerRef.current?.querySelector(`[data-page="${pageNumber}"]`);
        if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (onPageChange) onPageChange(pageNumber);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            let downloadBlob: Blob;
            if (blob) {
                downloadBlob = blob;
            } else if (fileId) {
                downloadBlob = await Oauth2GoogleService.getPdf(fileId);
            } else {
                return;
            }

            const url = URL.createObjectURL(downloadBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        const loadPdf = async () => {
            if (!fileId && !blob) return;
            
            setLoading(true);
            try {
                let arrayBuffer: ArrayBuffer;
                
                if (blob) {
                    arrayBuffer = await blob.arrayBuffer();
                } else if (fileId) {
                    const pdfBlob = await AgreementService.downloadPdf(fileId);
                    arrayBuffer = await pdfBlob.arrayBuffer();
                } else {
                    return;
                }

                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;
                setNumPages(pdf.numPages);
                
                if (containerRef.current) {
                    containerRef.current.innerHTML = '';
                }

                const newThumbnails: string[] = [];

                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);
                    
                    // Render Thumbnails
                    const thumbViewport = page.getViewport({ scale: 0.4 });
                    const thumbCanvas = document.createElement('canvas');
                    const thumbContext = thumbCanvas.getContext('2d');
                    thumbCanvas.height = thumbViewport.height;
                    thumbCanvas.width = thumbViewport.width;
                    await page.render({ canvasContext: thumbContext!, viewport: thumbViewport }).promise;
                    newThumbnails.push(thumbCanvas.toDataURL());

                    // Render Main Canvas
                    const viewport = page.getViewport({ scale });
                    const canvas = document.createElement('canvas');
                    canvas.setAttribute('data-page', i.toString());
                    canvas.className = 'shadow-2 mb-4 border-round';
                    canvas.style.display = 'block';
                    canvas.style.marginLeft = 'auto';
                    canvas.style.marginRight = 'auto';
                    canvas.style.maxWidth = '100%';
                    
                    const context = canvas.getContext('2d');
                    canvas.height = viewport.height;
                    canvas.width = viewport.width;

                    const renderTask = page.render({ canvasContext: context!, viewport });
                    renderTaskRef.current.push(renderTask);
                    await renderTask.promise;
                    
                    if (containerRef.current) {
                        containerRef.current.appendChild(canvas);
                    }
                }

                setThumbnails(newThumbnails);
                setLoading(false);
                if (onLoadComplete) onLoadComplete();
            } catch (error) {
                console.error('PdfCanvasViewer Error:', error);
                setLoading(false);
                if (onLoadError) onLoadError(error);
            }
        };

        loadPdf();

        return () => {
            renderTaskRef.current.forEach(task => {
                if (task && task.cancel) task.cancel();
            });
            renderTaskRef.current = [];
        };
    }, [fileId, blob, scale]);

    return (
        <div className="flex h-full w-full bg-gray-200 overflow-hidden relative">
            {loading && (
                <div className="absolute top-0 left-0 w-full h-full flex flex-column align-items-center justify-content-center bg-white-alpha-50 z-5">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" />
                    <p className="mt-3 text-700 font-medium">Đang xử lý tài liệu...</p>
                </div>
            )}

            {/* Main Viewer Area */}
            <div className="flex-grow-1 overflow-auto p-2 custom-scrollbar" style={{ height: '100%' }}>
                <div ref={containerRef} className="flex flex-column align-items-center">
                    {/* Canvases here */}
                </div>
                {!loading && numPages === 0 && (
                    <div className="flex flex-column align-items-center justify-content-center h-full text-500">
                        <i className="pi pi-file-pdf text-6xl mb-3"></i>
                        <p>File đang được chuẩn bị vui lòng thử lại trong giây lát</p>
                    </div>
                )}
            </div>

            {/* Sidebar Thumbnails */}
            <div 
                className="w-35rem border-left-1 border-gray-300 bg-white overflow-auto p-2 custom-scrollbar shadow-left z-2"
                style={{ height: '100%' }}
                ref={sidebarRef}
            >
                <div className="flex align-items-center justify-content-between border-bottom-1 border-gray-100 p-3 mb-2 bg-white sticky top-0 z-3">
                    <div className="text-sm font-bold text-600 uppercase">
                        Trang ({numPages})
                    </div>
                    <Button 
                        icon={downloading ? "pi pi-spin pi-spinner" : "pi pi-download"} 
                        className="p-button-rounded p-button-text p-button-sm"
                        tooltip="Tải tài liệu PDF"
                        tooltipOptions={{ position: 'left' }}
                        onClick={handleDownload}
                        disabled={downloading || loading}
                    />
                </div>
                <div className="flex flex-column gap-6 align-items-center p-3 py-4">
                    {thumbnails.map((thumb, idx) => (
                        <div 
                            key={idx} 
                            className={`cursor-pointer transition-all border-round-xl overflow-hidden border-2 mx-auto thumbnail-item flex-shrink-0 ${currentPage === idx + 1 ? 'border-primary shadow-4 scale-102' : 'border-300 hover:border-primary-400 opacity-90 hover:opacity-100'}`}
                            style={{ height: '450px', width: 'fit-content' }}
                            onClick={() => scrollToPage(idx + 1)}
                        >
                            <div className="relative bg-white h-full" style={{ minWidth: '100px' }}>
                                <img src={thumb} alt={`Trang ${idx + 1}`} className="h-full w-auto block" style={{ objectFit: 'contain', maxWidth: 'none' }} />
                                <div className={`absolute bottom-0 right-0 px-2 py-1 text-xs font-bold ${currentPage === idx + 1 ? 'bg-primary text-white' : 'bg-gray-200 text-700'} border-round-top-left shadow-2`}>
                                    {idx + 1}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                .shadow-left { box-shadow: -4px 0 15px rgba(0,0,0,0.08); }
                .custom-scrollbar::-webkit-scrollbar { width: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .scale-102 { transform: scale(1.02); }
                .thumbnail-item { position: relative; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
                .thumbnail-item:hover { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
            `}</style>
        </div>
    );
};
